<template>
	<!-- Skip Link for Keyboard Navigation — teleported before v-main for first-focus order -->
	<Teleport to="#skip-links-portal">
		<a href="#main-content" class="skip-link" @click.prevent="() => { const el = document.getElementById('main-content'); if (el) el.focus(); }">Skip to main content</a>
	</Teleport>
    <div id="main-content" tabindex="-1" role="main" aria-label="RISE Opportunities page content">
        <v-row justify="center" class="bg-white">
            <v-col cols="3" class="bg-color-teal-900">
                <v-row class="pa-10 h-screen" justify="center">
                    <div class="text-white">
                        <h1>
                            <LanguagePlaceholder is-html
                                default-text="RISE <br/> Opportunities" />
                        </h1>
                        <br/>
                         <!-- Language Dropdown -->
                        <label for="language-select" id="language-label">
                            <LanguagePlaceholder default-text="Select Asset Language:" />
                        </label>
                        <div class="language-dropdown-container">
                            <v-select
                                v-model="selectedLanguage"
                                :items="languages"
                                item-title="language"
                                item-value="iso"
                                variant="outlined"
                                density="comfortable"
                                class="language-select"
                                color="black"
                                bg-color="rgba(255, 255, 255, 0.1)"
                                hide-details
                                aria-label="Select language for RISE opportunities"
                                aria-describedby="language-label"
                                role="combobox"
                                tabindex="0"
                                @update:model-value="onLanguageChange"
                                @keydown.enter="onLanguageChange"
                                @keydown.space="onLanguageChange"
                            >
                                <template v-slot:prepend-inner>
                                    <v-icon color="white" class="mr-2" aria-hidden="true">mdi-translate</v-icon>
                                </template>
                            </v-select>
                        </div>
                        <br/>
                        <h4 class="general-heading">
                            <LanguagePlaceholder is-html
                                default-text="Incentive Period" />
                        </h4>
                        <h4>
                            <LanguagePlaceholder is-html
                                default-text="31 October 2026 – 29 January 2027" />
                        </h4>
                        <br/>
                        <br/>
                        <p>
                            <LanguagePlaceholder default-text="Download assets for each RISE bonus opportunity to promote them to your partners." />
                        </p>
                        <br>
                        <p>
                            <LanguagePlaceholder default-text="Using these assets to highlight bonus opportunities will encourage partners to increase their purchase volumes." />
                        </p>
                    </div>
                </v-row>
            </v-col>
            <v-col cols="8">
                <v-container class="pt-0">
                    <v-card v-for="category in assetCategories" :key="category.title" class="mb-2 elevation-0">
                        <v-card-title class="pa-6">
                            <h2 class="text-h6">{{ category.title }}</h2>
                        </v-card-title>
                        <v-row class="px-4" role="list" :aria-label="category.title">
                            <v-col v-for="card in category.cardContents" :key="card.title" cols="12" sm="6" md="4" role="listitem">
                                <v-card class="bonus-opportunity-card d-flex flex-column" role="article" :aria-label="`Bonus opportunity: ${card.title}`">
                                    <v-img :src="card.bannerURL" :alt="`Banner image for ${card.title}`" height="150px" cover class="flex-grow-0"></v-img>
                                    <v-card-title class="card-title pa-4">{{ card.title }}</v-card-title>
                                    <v-card-text class="card-details px-4 flex-grow-1">
                                        <div v-for="(detail, index) in card.details" :key="index" class="pb-3">
                                            <p v-if="detail.label"><strong><LanguagePlaceholder is-html :default-text="detail.label" /></strong></p>
                                            <p v-if="detail.value" :class="{ 'applicability-note': detail.highlight }"><LanguagePlaceholder is-html :default-text="detail.value" /></p>
                                        </div>
                                    </v-card-text>
                                    <v-toolbar border density="compact" v-for="action in card.actions" :key="action.title" class="flex-grow-0">
                                        <v-toolbar-title class="toolbar-title" :text="action.title"></v-toolbar-title>
                                        <template v-slot:append>
                                            <v-btn density="compact" icon @click="download(action.assetURL)"
                                                :aria-label="`Download ${card.title}`" tabindex="0"
                                                @keydown.enter.prevent="download(action.assetURL)" @keydown.space.prevent="download(action.assetURL)">
                                                <v-tooltip activator="parent" location="top">Download</v-tooltip>
                                                <v-icon class="toolbar-button" aria-hidden="true">mdi-download</v-icon>
                                            </v-btn>
                                        </template>
                                    </v-toolbar>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-container>
            </v-col>
            <v-col cols="1"></v-col>
        </v-row>
    </div>

</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import LanguagePlaceholder from '@/components/LanguagePlaceholder.vue'
import { useSystemStore } from '@/stores/system'

const systemStore = useSystemStore()
// Language dropdown state
const selectedLanguage = ref('EN') // Set default to EN
const languages = ref([])

const allowedUserTypes = ['Partner', 'Distributor', 'Dell Marketing', 'TP', 'PAM', 'TIC_Admin']

// Create a computed property for asset categories that automatically updates with user type and language changes
const assetCategories = computed(() => {
    const userType = systemStore.userDetail?.userType?.trim()
    const categories = allowedUserTypes.includes(userType) ? [riseOpportunities(selectedLanguage.value)] : []

    // Filter cardContents and actions within each category by userType.
    // If a cardContent or action has no userTypes array it is visible to all user types.
    return categories.map(category => ({
        ...category,
        cardContents: category.cardContents
            .filter(card => !card.userTypes || card.userTypes.includes(userType))
            .map(card => ({
                ...card,
                actions: card.actions.filter(action => !action.userTypes || action.userTypes.includes(userType))
            }))
    }))
})

// shortcut: placeholder assetURLs, replace with the real bonus opportunity URLs once supplied
const downloadAction = (assetURL) => ({
    title: 'Download',
    hasPreview: false,
    hasEmbed: false,
    embedCode: '',
    hasDownload: true,
    hasLink: false,
    assetURL
})

const promotionPeriod = { label: 'Promotion Period:', value: '31 Oct 2026 – 29 Jan 2027' }
const applicableTo = (value) => ({ value, highlight: true })

const riseOpportunities = (language) => ({
            title: 'RISE Opportunities',
            subtitle: '',
            cardContents: [
                {
                    title: 'First Purchase Bonus',
                    details: [
                        { value: 'Make your first purchase in a new product category and unlock <b>$250 for Server+</b> or <b>$500 for Storage SSO</b>!<br><br>Available to Partners who haven\'t purchased in the qualifying product category in the last 4 quarters.' },
                        promotionPeriod,
                        applicableTo('Applicable to new and existing Partners')
                    ],
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/Banners/RiseOpportunities/FPBonus.jpg',
                    actions: [
                        downloadAction(`https://dellpartnerincentives.com/siteassets/MarketingAssets/BonusOpportunities/FirstPurchaseBonus/FirstPurchaseBonus_${language}.pdf`)
                    ]
                },
                {
                    title: 'RISE Welcome Bonus',
                    details: [
                        { value: 'New to RISE? Start earning! Make your first qualifying purchase of <b>10 Client Solutions units</b> and unlock an additional <b>$250 Welcome Bonus</b>, on top of your core per-unit rewards!' },
                        promotionPeriod,
                        applicableTo('Applicable to new Partners')
                    ],
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/Banners/RiseOpportunities/RISEWelcomeBonus.jpg',
                    actions: [
                        downloadAction(`https://dellpartnerincentives.com/siteassets/MarketingAssets/BonusOpportunities/WelcomeBonus/WelcomeBonus_${language}.pdf`)
                    ]
                },
                {
                    title: 'Precision Workstation Bonus',
                    details: [
                        { value: 'Purchase Precision Workstations as SNS and unlock <b>$300</b> when you purchase <b>8 units</b> or <b>$600</b> when you purchase <b>14 units</b>!' },
                        promotionPeriod,
                        applicableTo('Applicable to new and existing Partners')
                    ],
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/Banners/RiseOpportunities/WorkstationBonus.jpg',
                    actions: [
                        downloadAction(`https://dellpartnerincentives.com/siteassets/MarketingAssets/BonusOpportunities/PrecisionWorkstationBonus/PrecisionWorkstationBonus_${language}.pdf`)
                    ]
                },
                {
                    title: 'RISE Growth Bonus',
                    details: [
                        { value: 'Grow your purchases by <b>15% or more</b> compared to the previous quarter and unlock an <b>additional 10% in rewards</b>, on top of your core RISE earnings!<br><br>Available to all Partners who meet the minimum purchase thresholds of <b>15 PCs, 1 Server or 1 Storage</b> unit in the qualifying category.' },
                        promotionPeriod,
                        applicableTo('Applicable to existing Partners')
                    ],
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/Banners/RiseOpportunities/GrowthBonus.jpg',
                    actions: [
                        downloadAction(`https://dellpartnerincentives.com/siteassets/MarketingAssets/BonusOpportunities/GrowthBonus/GrowthBonus_${language}.pdf`)
                    ]
                },
                {
                    title: 'RISE Reactivation Bonus',
                    details: [
                        { value: 'Welcome back! Haven\'t made a qualifying purchase in the previous quarter? Purchase just <b>3 qualifying units (PC, Server or Storage)</b> and unlock an additional <b>$50 Reactivation Bonus</b>!<br><br>Available to active Authorized RISE Partners.' },
                        promotionPeriod,
                        applicableTo('Applicable to existing Partners')
                    ],
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/Banners/RiseOpportunities/ReactivationBonus.jpg',
                    actions: [
                        downloadAction(`https://dellpartnerincentives.com/siteassets/MarketingAssets/BonusOpportunities/ReactivationBonus/ReactivationBonus_${language}.pdf`)
                    ]
                },
                {
                    title: 'Partner Presentations',
                    details: [
                        { value: 'This presentation outlines how to best position RISE in conversations with Partners, and highlights why joining this market-leading program is a smart move for their business.' }
                    ],
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/Banners/RiseOpportunities/PartnerPresentationsBanner.jpg',
                    actions: [
                        { ...downloadAction(`https://www.dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/PartnerIntroductionToRise/PartnerIntroToRise_${language}.pdf`), title: 'Partner Introduction To RISE' },
                        { ...downloadAction(`https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/PartnerIntroductoryGuide/PartnerIntroductoryGuide_${language}.pdf`), title: 'Partner Introductory Guide' }
                    ]
                }
            ]
        })

onMounted(() => {
    // Load available languages
    systemStore.getAllLanguages().then(result => {
        if (result.success && result.languages) {
            languages.value = result.languages
            // Set default language to English if none selected
            if (languages.value.length > 0 && !selectedLanguage.value) {
                selectedLanguage.value = 'EN'
            }
        } else {
            console.log('No languages found in response:', result)
        }
    }).catch(error => {
        console.log('Error loading languages:', error)
        // Fallback to default languages if API fails
        languages.value = [
            { language: 'English', iso: 'EN' },
            { language: 'French', iso: 'FR' },
            { language: 'German', iso: 'DE' },
            { language: 'Italian', iso: 'IT' },
            { language: 'Polish', iso: 'PL' },
            { language: 'Spanish', iso: 'ES' },
            { language: 'Turkish', iso: 'TR' },
            { language: 'Czech', iso: 'CZ' },
            { language: 'Hungarian', iso: 'HU' },
            { language: 'Romanian', iso: 'RO' }
        ]
        if (!selectedLanguage.value) {
            selectedLanguage.value = 'EN'
        }
    })
})

const download = (url) => {
    window.open(url).focus()
}

// Language change handler
const onLanguageChange = (languageIso) => {
    if (languageIso) {
        // Store the selected language - the computed property will automatically update
        selectedLanguage.value = languageIso

        // Show a brief message to confirm language change
        systemStore.showMessage(`Showing assets in ${languageIso}`, {
            timeout: 2000,
            color: 'success'
        })
    }
}
</script>

<style scoped>
.bonus-opportunity-card {
    height: 100%;
}

.card-title {
    font-size: 14px;
    font-weight: 500;
    color: #222c2c;
    white-space: normal;
}

.card-details {
    font-size: 12px;
    color: #252424;
    line-height: 1.4;
}

.applicability-note {
    color: #b71c1c;
    font-weight: 700;
}

.toolbar-title {
    font-size: 12px;
    font-weight: 500;
    color: #252424;
}

.toolbar-button {
    color: #85133F;
}

.language-dropdown-container {
    margin-bottom: 16px;
}

.language-select {
    background-color: rgba(255, 255, 255, 0.1) !important;
}

.language-select :deep(.v-field) {
    background-color: rgba(255, 255, 255, 0.1) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.language-select :deep(.v-field__input) {
    color: white !important;
}

.language-select :deep(.v-field__label) {
    color: rgba(255, 255, 255, 0.8) !important;
}

.language-select :deep(.v-field__append-inner) {
    color: white !important;
}

.language-select :deep(.v-select__selection) {
    color: white !important;
}

.language-select :deep(.v-list) {
    background-color: #0d2155 !important;
}

.language-select :deep(.v-list-item) {
    color: white !important;
}

.language-select :deep(.v-list-item:hover) {
    background-color: rgba(255, 255, 255, 0.1) !important;
}
</style>
